import { useState, useEffect } from 'react';

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  targetElement?: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'above';
}

const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'project-intro',
    title: 'Welcome to the Surgical Training Platform',
    description: 'This Learning Analytics Platform for Simulation-Based Training helps medical professionals enhance their surgical skills through interactive video sessions, peer collaboration, and AI-powered assessments.',
    position: 'bottom'
  },
  {
    id: 'dashboard-overview',
    title: 'Your Training Dashboard',
    description: 'This is your central hub where you can access all training materials, track your progress, and collaborate with other medical professionals. Everything you need for your surgical training journey is here.',
    position: 'bottom'
  },
  {
    id: 'navigation-tabs',
    title: 'Navigation Tabs',
    description: 'Use these tabs to navigate between different sections: Dashboard (main overview), Assessment Summary (AI reports for instructors), and History (your activity timeline).',
    targetElement: '.tab-navigation',
    position: 'bottom'
  },
  {
    id: 'session-cards',
    title: 'Training Sessions',
    description: 'Each card represents a surgical training session. Click "Start Session" to begin watching videos, leaving timestamped comments, and collaborating with peers. You can see the session status, date, and description here.',
    targetElement: '.sessions-grid',
    position: 'above'
  },
  {
    id: 'refresh-button',
    title: 'Refresh Content',
    description: 'Use this button to refresh and load the latest training sessions and updates. This ensures you always have access to the most current content.',
    targetElement: '.refresh-button',
    position: 'bottom'
  },
  {
    id: 'start-session-button',
    title: 'Ready to Begin?',
    description: 'Click the "Start Session" button on this first training session to begin your surgical training journey. You\'ll enter an interactive video environment where you can watch procedures and collaborate with other medical professionals.',
    targetElement: '.first-session-card',
    position: 'right'
  }
];

const ONBOARDING_SESSION_KEY = 'surgical_training_onboarding_session';
const ONBOARDING_PERMANENT_KEY = 'surgical_training_onboarding_dont_show_again';

export const useOnboarding = () => {
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);

  useEffect(() => {
    // Check if user has permanently disabled onboarding
    const dontShowAgain = localStorage.getItem(ONBOARDING_PERMANENT_KEY);
    if (dontShowAgain === 'true') {
      setHasCompletedOnboarding(true);
      return;
    }

    // Development mode: Show onboarding on every page reload
    const isDevelopment = import.meta.env.DEV;

    if (isDevelopment) {
      // In development, always show onboarding (unless permanently disabled)
      setIsOnboardingActive(true);
      setHasCompletedOnboarding(false);
    } else {
      // Production mode: Check if user has already seen onboarding in this session
      const completedThisSession = sessionStorage.getItem(ONBOARDING_SESSION_KEY);

      if (completedThisSession === 'true') {
        setHasCompletedOnboarding(true);
      } else {
        // Show onboarding on every login/session
        setIsOnboardingActive(true);
        setHasCompletedOnboarding(false);
      }
    }
  }, []);

  const nextStep = () => {
    if (currentStepIndex < ONBOARDING_STEPS.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      completeOnboarding();
    }
  };

  const previousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  const skipOnboarding = () => {
    completeOnboarding();
  };

  const completeOnboarding = () => {
    setIsOnboardingActive(false);
    setHasCompletedOnboarding(true);
    sessionStorage.setItem(ONBOARDING_SESSION_KEY, 'true');
  };

  const dontShowAgain = () => {
    localStorage.setItem(ONBOARDING_PERMANENT_KEY, 'true');
    setIsOnboardingActive(false);
    setHasCompletedOnboarding(true);
  };

  const resetOnboarding = () => {
    sessionStorage.removeItem(ONBOARDING_SESSION_KEY);
    localStorage.removeItem(ONBOARDING_PERMANENT_KEY);
    setCurrentStepIndex(0);
    setIsOnboardingActive(true);
    setHasCompletedOnboarding(false);
  };

  const currentStep = ONBOARDING_STEPS[currentStepIndex];
  const isLastStep = currentStepIndex === ONBOARDING_STEPS.length - 1;

  return {
    isOnboardingActive,
    currentStep,
    currentStepIndex,
    totalSteps: ONBOARDING_STEPS.length,
    isLastStep,
    hasCompletedOnboarding,
    nextStep,
    previousStep,
    skipOnboarding,
    completeOnboarding,
    dontShowAgain,
    resetOnboarding
  };
};